/**
 * Il segno che marca un soggetto o un tipo che non esiste più.
 *
 * ## Perché serve
 *
 * Da quando esistono le scadenze orfane, `scadenza.soggetto.denominazione` può
 * essere un **nome congelato**: sembra un nome vivo, si legge come un nome
 * vivo, ma dietro non c'è nessuna scheda. Senza questo segno «Mario Rossi» in
 * un elenco è indistinguibile dal Mario Rossi che è ancora in anagrafica, e
 * l'unico modo per accorgersene sarebbe cliccarci sopra e non trovare niente.
 *
 * ## Le regole che rispetta
 *
 * ⚠️ **Vincolo fisso del progetto: mai il colore da solo.** Qui i segnali sono
 * tre — la parola ELIMINATO scritta per esteso, la forma della catena spezzata,
 * e solo in aggiunta l'ambra. Chi non distingue i colori legge la parola; chi
 * guarda di fretta vede la catena.
 *
 * L'ambra e la catena spezzata non sono scelte a caso: sono **le stesse**
 * dell'effetto `SCOLLEGATO` nell'elenco delle conseguenze del dialogo di
 * eliminazione. Chi ha scelto «Conserva le scadenze» ha visto quel simbolo
 * mentre decideva, e lo ritrova sulle righe che ne sono nate.
 */

/** Catena spezzata: la riga c'è ancora, il legame no. */
const CATENA_SPEZZATA = (
  <path d="M6.6 9.4 5.1 10.9a2.6 2.6 0 0 1-3.7-3.7l1.5-1.5M9.4 6.6l1.5-1.5a2.6 2.6 0 0 1 3.7 3.7l-1.5 1.5M8 1.4v1.8M1.4 8h1.8M14.6 8h-1.8M8 14.6v-1.8" />
);

const SPIEGAZIONE = {
  soggetto:
    'Questo soggetto è stato eliminato. Il nome resta scritto sulla scadenza, ' +
    'ma non c’è più nessuna scheda da aprire e la scadenza non si può più ' +
    'filtrare per soggetto.',
  tipo:
    'Questo tipo è stato eliminato dal catalogo. Il nome resta scritto sulla ' +
    'scadenza, ma non c’è più nessun tipo da aprire. Il preavviso continua a ' +
    'funzionare: è copiato sulla scadenza, non letto dal tipo.',
} as const;

export function SegnoEliminato({ cosa }: { cosa: 'soggetto' | 'tipo' }) {
  return (
    <span className="badge badge-eliminato" title={SPIEGAZIONE[cosa]}>
      {/* Decorativa: la parola accanto dice già la stessa cosa, e farla leggere
          due volte da un lettore di schermo sarebbe solo rumore. */}
      <svg
        width="11"
        height="11"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {CATENA_SPEZZATA}
      </svg>
      {/* Visibile c'è la sola parola «eliminato», che in una colonna intitolata
          «Soggetto» basta. Per chi ascolta la riga fuori contesto il sostantivo
          va detto: «eliminato» da solo non dice cosa. */}
      <span className="solo-lettori-schermo">{cosa === 'soggetto' ? 'Soggetto ' : 'Tipo '}</span>
      eliminato
    </span>
  );
}
